import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { TrendingUp, Scale, Activity, Camera, Minus } from "lucide-react";

export const ClientViewSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const clientStats = [
    { label: "Peso", value: "72.3 kg", change: "-2.1", icon: Scale },
    { label: "% Grasa", value: "18.4%", change: "-1.2", icon: Activity },
    { label: "Cintura", value: "84 cm", change: "-3", icon: Minus },
    { label: "Δ Abdomen", value: "-2.1", change: "", icon: TrendingUp },
  ];

  const consultationPoints = [
    "Mantener déficit calórico de 300 kcal",
    "Aumentar proteína a 1.8g/kg",
    "Nuevo plan de hidratación",
  ];

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0808] to-[#050505]" />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-block text-[#E05D3A] text-sm font-semibold tracking-widest uppercase mb-4"
            >
              Vista del Cliente
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6"
            >
              Tu cliente ve su progreso.{" "}
              <span className="text-gradient">Tú te ves profesional.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-8"
            >
              Cada cliente recibe un link único donde puede ver su evolución, puntos 
              clave de cada consulta y fotos de progreso. Sin crear cuenta. Sin complejidad.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {["Sin registro", "Link privado", "Vista móvil optimizada"].map((item) => (
                <span
                  key={item}
                  className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400"
                >
                  {item}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative flex justify-center"
          >
            {/* Glow behind phone */}
            <div className="absolute inset-0 bg-[#E05D3A]/20 blur-[100px] rounded-full" />
            
            {/* Phone frame */}
            <div className="relative w-[280px] md:w-[320px] float-animation">
              <div className="relative rounded-[40px] bg-gradient-to-b from-zinc-800 to-zinc-900 p-3 shadow-2xl">
                {/* Screen */}
                <div className="rounded-[32px] bg-[#0a0a0a] overflow-hidden">
                  {/* Status bar */}
                  <div className="h-8 bg-black flex items-center justify-center">
                    <div className="w-20 h-5 bg-zinc-900 rounded-full" />
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-4">
                    {/* Header */}
                    <div className="text-center pb-3 border-b border-white/10">
                      <p className="text-zinc-500 text-xs mb-1">Tu progreso</p>
                      <h3 className="text-white font-outfit font-medium">¡Hola, María!</h3>
                    </div>

                    {/* Stats grid */}
                    <div className="grid grid-cols-2 gap-2">
                      {clientStats.map((stat) => (
                        <div
                          key={stat.label}
                          className="p-3 rounded-xl bg-white/5 border border-white/10"
                        >
                          <div className="flex items-center gap-1 mb-1">
                            <stat.icon className="w-3 h-3 text-[#E05D3A]" />
                            <span className="text-[10px] text-zinc-500">{stat.label}</span>
                          </div>
                          <p className="text-white font-semibold text-sm">{stat.value}</p>
                          {stat.change && (
                            <span className="text-[10px] text-green-400">{stat.change}</span>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Last consultation */}
                    <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                      <p className="text-[10px] text-zinc-500 mb-2">Última consulta</p>
                      <ul className="space-y-1.5">
                        {consultationPoints.map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-[11px] text-zinc-300">
                            <span className="text-[#E05D3A] mt-0.5">•</span>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Trend indicator */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-[#E05D3A]/20 to-transparent border border-[#E05D3A]/20">
                      <span className="text-[11px] text-zinc-300">Tendencia</span>
                      <span className="text-[11px] text-[#E05D3A] font-medium flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> Positiva
                      </span>
                    </div>

                    {/* Photos indicator */}
                    <div className="flex items-center gap-2">
                      <Camera className="w-4 h-4 text-zinc-500" />
                      <span className="text-[11px] text-zinc-500">3 check-ins con fotos</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
